import { ExtensionsIcon, Modal, SettingsIcon } from "@carbon6/c6-frontend-design";
import { useEffect, useState } from "react";
import { HatModalHeader } from "@/components/modal";
import { GeneralSettingsPanel } from "./GeneralSettingsPanel";
import { McpConnectPanel } from "./McpConnectPanel";
import styles from "./SettingsModal.module.css";

// 계정 그룹이다. 모달의 유일한 그룹이고, 유저 이름(모노그램 아바타) 항목과 일반 설정·MCP 연결로 구성한다.
// MCP 연결은 계정마다 자기 에이전트를 연결하는 일이다. 그래서 이 그룹에 둔다.
const settingsItemKeys = {
  profile: "account-profile",
  general: "account-general",
  mcp: "account-mcp",
} as const;

type SettingsModalProps = {
  open: boolean;
  onClose: () => void;
  userName: string;
};

// 설정 모달이다: 헤더(제목·닫기) + 좌측 메뉴(계정 항목) + 우측 내용 패널. 제목과 닫기는
// 앱 공용 HatModalHeader 가 소유한다. 닫기는 backdrop·Escape 와 같은 onClose 를 호출한다.
// 열림 상태는 호출자(PageBase)가 소유한다.
export function SettingsModal({ open, onClose, userName }: SettingsModalProps) {
  const [activeItem, setActiveItem] = useState<string>(settingsItemKeys.profile);

  // 닫아도 컴포넌트가 마운트된 상태라 활성 항목이 남는다. 그래서 열 때마다 첫 항목으로 되돌린다.
  useEffect(() => {
    if (open) {
      setActiveItem(settingsItemKeys.profile);
    }
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      // 폭·높이는 style 로만 준다. Dialog 는 styled-components 라서 CSS module 보다 늦게 주입되고,
      // 같은 특정도에서 우선한다. 높이를 창에 고정해야 내용이 짧은 항목(프로필)에서도 창이 줄지
      // 않는다. 헤더는 그 안에 공간을 가지고, 남은 높이는 Modal.Body 가 채운다.
      style={{ maxWidth: "min(94vw, 62.4rem)", height: "min(80vh, 44rem)" }}
    >
      <HatModalHeader title="설정" close />
      {/* 스크롤은 좌측 메뉴와 우측 패널이 각자 소유한다. Body 전체가 스크롤하면 메뉴도
          내용과 함께 위로 이동한다. 여백도 두 영역이 각자 가진다. */}
      <Modal.Body padding={false} scrollable={false}>
        <div className={styles.layout}>
          <nav className={styles.nav} aria-label="설정 메뉴">
            {/* 유저 이름(모노그램 아바타) + 일반 설정 + MCP 연결. 모든 행의 높이가 같다. */}
            <div className={styles.navSection}>
              <button
                type="button"
                className={styles.navItem}
                data-active={activeItem === settingsItemKeys.profile ? "true" : "false"}
                aria-current={activeItem === settingsItemKeys.profile ? "page" : undefined}
                onClick={() => setActiveItem(settingsItemKeys.profile)}
              >
                <span className={styles.monogram} aria-hidden="true">
                  {monogram(userName)}
                </span>
                <span className={styles.itemLabel}>{userName}</span>
              </button>
              <button
                type="button"
                className={styles.navItem}
                data-active={activeItem === settingsItemKeys.general ? "true" : "false"}
                aria-current={activeItem === settingsItemKeys.general ? "page" : undefined}
                onClick={() => setActiveItem(settingsItemKeys.general)}
              >
                <span className={styles.navIcon} aria-hidden="true">
                  <SettingsIcon />
                </span>
                <span className={styles.itemLabel}>일반 설정</span>
              </button>
              <button
                type="button"
                className={styles.navItem}
                data-active={activeItem === settingsItemKeys.mcp ? "true" : "false"}
                aria-current={activeItem === settingsItemKeys.mcp ? "page" : undefined}
                onClick={() => setActiveItem(settingsItemKeys.mcp)}
              >
                <span className={styles.navIcon} aria-hidden="true">
                  <ExtensionsIcon />
                </span>
                <span className={styles.itemLabel}>MCP 연결</span>
              </button>
            </div>
          </nav>
          {/* 우측 패널이다. 활성 항목별 내용을 표시한다. 프로필 항목은 아직 비어 있다. */}
          <section className={styles.panel} aria-label="설정 내용">
            {activeItem === settingsItemKeys.general ? <GeneralSettingsPanel /> : null}
            {activeItem === settingsItemKeys.mcp ? <McpConnectPanel /> : null}
          </section>
        </div>
      </Modal.Body>
    </Modal>
  );
}

function monogram(name: string): string {
  return name.trim().charAt(0).toUpperCase() || "?";
}
